import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { EXCHANGES } from "../config/exchanges";
import { EXCHANGE_META } from "../config/exchangeMeta";
import {
  BLOCKS_LOTTERY_ENABLED,
  BLOCKS_TELEGRAM_BOT_ENABLED,
  BLOCKS_TELEGRAM_MINI_APP_BOT,
} from "../config/blocks";
import type { Connector, ExchangeId, OrderBookSnapshot, Price } from "../types";
import { usePriceStore } from "../state/usePrice";
import {
  clearStoredReferralCode,
  getStoredReferralCode,
  rememberReferralCode,
} from "../lib/referralStorage";
import { buildMiniAppStartParamWithReferral, extractReferralFromMiniAppStartParam } from "../lib/miniAppReferral";
import { bootstrapTelegramMiniAppLogin } from "../lib/telegramMiniAppLogin";
import { PriceCard } from "./components/PriceCard";
import { ExchangeComparisonTable } from "./components/ExchangeComparisonTable";
import { ConversionMatrix } from "./components/ConversionMatrix";
import {
  BotIcon,
  SendIcon,
  PhoneIcon,
  GlobeIcon,
  ChartLineIcon,
  CompareIcon,
  CalculatorIcon,
  PurchaseBagIcon,
  RotateCcwIcon,
} from "./components/icons";
import {
  PublicBlocksExperienceProvider,
  usePublicBlocksExperience,
} from "./components/blocks/usePublicBlocksExperience";
import { LotteryBottomNav, type LotteryNavActiveTab } from "./components/lottery/LotteryBottomNav";

const DailyMeme = lazy(async () => ({ default: (await import("./components/DailyMeme")).DailyMeme }));
const LotteryPanel = lazy(async () => ({ default: (await import("./components/lottery/LotteryPanel")).LotteryPanel }));
const LotteryAdminPanel = lazy(async () => ({ default: (await import("./components/lottery/LotteryAdminPanel")).LotteryAdminPanel }));
const LotteryLeaderboardPage = lazy(async () => ({
  default: (await import("./components/lottery/LotteryLeaderboardPage")).LotteryLeaderboardPage,
}));
const LivePriceComparison = lazy(async () => ({ default: (await import("./components/LivePriceComparison")).LivePriceComparison }));
const WalletPanel = lazy(async () => ({ default: (await import("./components/WalletPanel")).WalletPanel }));

function SectionSkeleton({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-800/70 bg-slate-900/40 p-6 text-sm text-slate-200" role="status" aria-live="polite">
      {children}
    </div>
  );
}

type ConnectorsMap = Record<ExchangeId, Connector>;
type SectionId = "live" | "comparison" | "calculator" | "lottery-bitcoin" | "account";
const SECTION_IDS: readonly SectionId[] = ["live", "comparison", "calculator", "lottery-bitcoin", "account"] as const;
const DEFAULT_SECTION: SectionId = "live";

// Sections that belong to the Bitcoin lottery. While `BLOCKS_LOTTERY_ENABLED` is off they stay out
// of routing entirely, so a stale `#lottery-bitcoin`, `#lottery-leaderboard`, `#account` or `?ref=`
// link lands on the default tab instead of an empty page.
const LOTTERY_SECTION_IDS: readonly SectionId[] = ["lottery-bitcoin", "account"] as const;

function isSectionId(value: string | null | undefined): value is SectionId {
  if (!value) {
    return false;
  }
  return (SECTION_IDS as readonly string[]).includes(value);
}

function isNavigableSection(value: string | null | undefined): value is SectionId {
  if (!isSectionId(value)) {
    return false;
  }
  if (!BLOCKS_LOTTERY_ENABLED && LOTTERY_SECTION_IDS.includes(value)) {
    return false;
  }
  return true;
}

const chartColors: Record<ExchangeId, string> = {
  nobitex: "#7c3aed",
  wallex: "#0ea5e9",
  bitpin: "#22c55e",
};

const LOTTERY_DISABLED_BADGE = "غیرفعال";
const LOTTERY_DISABLED_HINT = "قرعه‌کشی بیت‌کوین فعلاً غیرفعال است.";

const TELEGRAM_CHANNEL_URL = "https://t.me/irancoin";
const TELEGRAM_CONTACT_URL = "https://t.me/irancoin?direct";
const CRYPTOCHANGE_WEB_URL = "https://cryptochange.ir/";

function resolveTelegramBotUrl(): string {
  const handle = BLOCKS_TELEGRAM_MINI_APP_BOT.replace(/^@/, "").trim();
  return handle ? `https://t.me/${handle}` : "https://t.me/CryptoChangeIRBot";
}

const TELEGRAM_BOT_URL = resolveTelegramBotUrl();

function computeMid(price: Price | undefined, book: OrderBookSnapshot | undefined): number | null {
  if (price?.bid && price?.ask) {
    return (price.bid + price.ask) / 2;
  }
  if (book?.bids?.length && book?.asks?.length) {
    return (book.bids[0].price + book.asks[0].price) / 2;
  }
  if (price?.last) {
    return price.last;
  }
  return null;
}

export default function App() {
  const path = typeof window !== "undefined" ? window.location.pathname.replace(/\/+$/, "") : "";
  const isLotteryAdmin = path === "/lottery-admin";
  // With the lottery off the legacy /lottery-leaderboard URL is just another way into a disabled
  // feature, so it falls through to the normal app on the default tab.
  const isLegacyLotteryLeaderboard = BLOCKS_LOTTERY_ENABLED && path === "/lottery-leaderboard";
  if (isLotteryAdmin) {
    return (
      <Suspense fallback={<SectionSkeleton>در حال بارگذاری پنل ادمین...</SectionSkeleton>}>
        <LotteryAdminPanel />
      </Suspense>
    );
  }

  return (
    <MainApp
      initialLotteryPageVariant={isLegacyLotteryLeaderboard ? "leaderboard" : "panel"}
      forceActiveSection={isLegacyLotteryLeaderboard ? "lottery-bitcoin" : undefined}
      legacyLotteryLeaderboardPath={isLegacyLotteryLeaderboard}
    />
  );
}


function MainAppContent({
  initialLotteryPageVariant = "panel",
  forceActiveSection,
  legacyLotteryLeaderboardPath = false,
}: {
  initialLotteryPageVariant?: "panel" | "leaderboard" | "account";
  forceActiveSection?: SectionId;
  legacyLotteryLeaderboardPath?: boolean;
}) {
  const [connectors, setConnectors] = useState<ConnectorsMap | null>(null);
  const [connectorsError, setConnectorsError] = useState<string | null>(null);
  const [connectorsVersion, setConnectorsVersion] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionId>(DEFAULT_SECTION);
  const [lotteryPageVariant, setLotteryPageVariant] = useState<"panel" | "leaderboard" | "account">(
    initialLotteryPageVariant,
  );
  const currentSection = forceActiveSection ?? activeSection;
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const experience = usePublicBlocksExperience();
  const prices = usePriceStore((state) => state.prices);
  const health = usePriceStore((state) => state.health);
  const change = usePriceStore((state) => state.change);
  const orderBooks = usePriceStore((state) => state.orderBooks);
  const marketMeta = usePriceStore((state) => state.marketMeta);
  const stale = usePriceStore((state) => state.stale);
  const staleBadges = usePriceStore((state) => state.staleBadge);
  const initFromCache = usePriceStore((state) => state.initFromCache);
  const setPrice = usePriceStore((state) => state.setPrice);
  const setHealth = usePriceStore((state) => state.setHealth);
  const refreshStale = usePriceStore((state) => state.refreshStale);
  const resetAll = usePriceStore((state) => state.resetAll);
  const tradeMode: "buy" | "sell" = "buy";

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const webApp = window.Telegram?.WebApp ?? null;
    const startParamCandidate = (() => {
      const unsafeStart = typeof webApp?.initDataUnsafe?.start_param === "string"
        ? webApp?.initDataUnsafe?.start_param
        : null;
      if (unsafeStart) {
        return unsafeStart;
      }
      const rawInitData = typeof webApp?.initData === "string" ? webApp?.initData : null;
      if (!rawInitData) {
        return null;
      }
      try {
        const initParams = new URLSearchParams(rawInitData);
        return initParams.get("tgWebAppStartParam");
      } catch {
        return null;
      }
    })();

    let storedReferral: string | null = null;
    const referralFromMiniApp = extractReferralFromMiniAppStartParam(startParamCandidate);
    if (referralFromMiniApp) {
      storedReferral = rememberReferralCode(referralFromMiniApp);
    }

    const candidateKeys = ["ref", "referral", "refcode"] as const;
    const params = new URLSearchParams(window.location.search);
    let referralRaw: string | null = null;
    for (const key of candidateKeys) {
      const value = params.get(key);
      if (value) {
        referralRaw = value;
        break;
      }
    }

    if (!referralRaw && window.location.hash) {
      const hashValue = window.location.hash.replace(/^#/, "");
      const hashQuery = hashValue.includes("?") ? hashValue.slice(hashValue.indexOf("?") + 1) : hashValue;
      if (hashQuery) {
        const hashParams = new URLSearchParams(hashQuery);
        for (const key of candidateKeys) {
          const value = hashParams.get(key);
          if (value) {
            referralRaw = value;
            break;
          }
        }
      }
      if (!referralRaw) {
        const match = hashValue.match(/(?:^|[?&#])ref=([^&#]+)/i);
        if (match && match[1]) {
          try {
            referralRaw = decodeURIComponent(match[1]);
          } catch {
            referralRaw = match[1];
          }
        }
      }
    }

    if (referralRaw) {
      storedReferral = rememberReferralCode(referralRaw);
    }

    if (!storedReferral) {
      storedReferral = getStoredReferralCode();
    }

    setReferralCode(storedReferral);
  }, []);

  useEffect(() => {
    void initFromCache();
  }, [initFromCache]);

  useEffect(() => {
    // Attempt a silent Telegram MiniApp login globally (works on any section)
    void bootstrapTelegramMiniAppLogin();
  }, []);

  useEffect(() => {
    refreshStale();
    const interval = window.setInterval(() => {
      refreshStale();
    }, 5_000);
    return () => window.clearInterval(interval);
  }, [refreshStale]);

  const handleHardReset = useCallback(async () => {
    const confirmed = window.confirm(
      "آیا از بازنشانی کامل برنامه و پاک کردن تمام داده‌های ذخیره‌شده مطمئن هستید؟"
    );
    if (!confirmed) {
      return;
    }

    try {
      await resetAll();

      try {
        clearStoredReferralCode();
      } catch (error) {
        console.warn("[App] Failed to clear referral storage during hard reset", error);
      }

      if (typeof window !== "undefined") {
        try {
          const storage = window.localStorage;
          const explicitKeys = [
            "lottery.telegramLoginState",
            "lottery.lastSatToToman",
            "lottery.blockRewards",
            "lottery.blockRewardCache",
            "lottery.lightningAddress",
            "lottery.referralCode",
          ];
          const prefixes = ["lottery.", "lottery.admin."];
          const keysToRemove = new Set<string>(explicitKeys);
          for (let index = 0; index < storage.length; index += 1) {
            const key = storage.key(index);
            if (!key) {
              continue;
            }
            if (prefixes.some((prefix) => key.startsWith(prefix))) {
              keysToRemove.add(key);
            }
          }
          keysToRemove.forEach((key) => {
            try {
              storage.removeItem(key);
            } catch (removeError) {
              console.warn(`[App] Failed to remove localStorage key ${key}`, removeError);
            }
          });
        } catch (error) {
          console.warn("[App] Unable to access localStorage during hard reset", error);
        }

        try {
          window.sessionStorage?.removeItem("lottery-admin-token");
        } catch (error) {
          console.warn("[App] Unable to access sessionStorage during hard reset", error);
        }
      }

      if ("caches" in window) {
        const cacheNames = await window.caches.keys();
        await Promise.all(cacheNames.map((name) => window.caches.delete(name)));
      }

      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }
    } catch (error) {
      console.error("[App] Failed to perform hard reset:", error);
    } finally {
      window.location.reload();
    }
  }, [resetAll]);

  useEffect(() => {
    let cancelled = false;
    let retryCount = 0;
    const maxRetries = 3;

    const loadConnectors = () => {
      if (cancelled) return;
      setConnectors(null);
      setConnectorsError(null);
      import("../adapters")
        .then((module) => {
          if (!cancelled) setConnectors(module.CONNECTORS);
        })
        .catch((error) => {
          console.error("[App] Failed to load connectors:", error);
          if (!cancelled && retryCount < maxRetries) {
            retryCount++;
            // Exponential backoff
            setTimeout(loadConnectors, Math.min(1000 * Math.pow(2, retryCount), 8000));
          } else if (!cancelled) {
            setConnectorsError("load_connectors_failed");
            (Object.keys(EXCHANGES) as ExchangeId[]).forEach((exchange) => {
              setHealth(exchange, "DISCONNECTED");
            });
          }
        });
    };

    loadConnectors();
    return () => {
      cancelled = true;
    };
  }, [connectorsVersion, setHealth]);

  const handleRetryConnectors = useCallback(() => {
    setConnectorsVersion((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!connectors) return;

    let disposed = false;
    const teardowns: Array<() => void> = [];

    const connectAll = () => {
      (Object.keys(connectors) as ExchangeId[]).forEach((exchange) => {
        const connect = connectors[exchange];
        connect(
          (price) => {
            setPrice(price);
          },
          (status) => {
            setHealth(exchange, status);
          },
        )
          .then((teardown) => {
            if (disposed) {
              teardown();
            } else {
              teardowns.push(teardown);
            }
          })
          .catch(() => {
            setHealth(exchange, "DISCONNECTED");
          });
      });
    };

    const idle = (window as any).requestIdleCallback?.(() => {
      if (!disposed) connectAll();
    });
    if (!idle) {
      const timeout = setTimeout(() => {
        if (!disposed) connectAll();
      }, 200);
      teardowns.push(() => clearTimeout(timeout));
    }

    return () => {
      disposed = true;
      if (idle && (window as any).cancelIdleCallback) {
        (window as any).cancelIdleCallback(idle);
      }
      teardowns.forEach((teardown) => teardown());
    };
  }, [connectors, setHealth, setPrice]);

  // Add debug logging for health status changes
  useEffect(() => {
    console.debug("[App] Health status:", health);
  }, [health]);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      console.debug("[App] Browser went online");
      // Avoid hard reloads that can interrupt flows (e.g. Telegram login)
      // Instead, trigger connector retries and stale refresh.
      try {
        refreshStale();
      } catch {}
      try {
        handleRetryConnectors();
      } catch {}
    };

    const handleOffline = () => {
      console.debug("[App] Browser went offline");
      setHealth("nobitex", "DISCONNECTED");
      setHealth("wallex", "DISCONNECTED");
      setHealth("bitpin", "DISCONNECTED");
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [setHealth, refreshStale, handleRetryConnectors]);

  const midPrices = useMemo(() => {
    const result: Partial<Record<ExchangeId, number>> = {};
    (Object.keys(EXCHANGES) as ExchangeId[]).forEach((exchange) => {
      const price = prices[exchange];
      if (!price || (price.ts ?? 0) <= 0) return;
      if (stale[exchange]) return;
      const mid = computeMid(price, orderBooks[exchange]);
      if (typeof mid === "number" && Number.isFinite(mid)) {
        result[exchange] = mid;
      }
    });
    return result;
  }, [orderBooks, prices, stale]);

  const averageMid = useMemo(() => {
    const values = Object.values(midPrices).filter((v): v is number => typeof v === "number" && Number.isFinite(v));
    if (!values.length) return null;
    return values.reduce((acc, v) => acc + v, 0) / values.length;
  }, [midPrices]);

  const averageSatToToman = useMemo(() => {
    if (typeof averageMid !== "number" || !Number.isFinite(averageMid) || averageMid <= 0) {
      return null;
    }
    return averageMid / 100_000_000;
  }, [averageMid]);

  const averageUsdt = useMemo(() => {
    const values = (Object.keys(EXCHANGES) as ExchangeId[])
      .filter((exchange) => !stale[exchange])
      .map((exchange) => marketMeta[exchange]?.usdtToman)
      .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
    if (!values.length) return null;
    return values.reduce((acc, v) => acc + v, 0) / values.length;
  }, [marketMeta, stale]);

  const comparisonRows = useMemo(() => {
    return (Object.keys(EXCHANGES) as ExchangeId[]).map((exchange) => {
      const price = prices[exchange];
      const hasFreshPrice = typeof price?.ts === "number" && price.ts > 0 && !stale[exchange];
      const mid = hasFreshPrice ? computeMid(price, undefined) : null;
      const meta = EXCHANGE_META[exchange];
      return {
        id: exchange,
        name: EXCHANGES[exchange].label,
        logo: EXCHANGES[exchange].logo,
        logoWebp: EXCHANGES[exchange].logoWebp,
        link: EXCHANGES[exchange].referralUrl,
        mid: mid ?? undefined,
        withdrawalFeeBtc: meta.withdrawalFeeBtc,
        supportsLightning: meta.supportsLightning,
        lightningStatus: meta.lightningStatus,
        lightningNote: meta.lightningNote,
        withdrawalLimits: meta.withdrawalLimits,
        feeSummary: meta.feeSummary,
        takerFee: meta.feeSummary.maxTakerFee,
        effectiveToman: undefined,
      };
    });
  }, [prices, stale]);

  const livePriceRows = useMemo(() => {
    return (Object.keys(EXCHANGES) as ExchangeId[]).map((exchange) => {
      const price = prices[exchange];
      const book = orderBooks[exchange];
      const isFresh = !stale[exchange];
      const bestBid = isFresh && typeof price?.bid === "number" && Number.isFinite(price.bid)
        ? price.bid
        : isFresh
          ? book?.bids?.[0]?.price
          : undefined;
      const bestAsk = isFresh && typeof price?.ask === "number" && Number.isFinite(price.ask)
        ? price.ask
        : isFresh
          ? book?.asks?.[0]?.price
          : undefined;
      const last = isFresh && typeof price?.last === "number" ? price.last : undefined;
      return {
        id: exchange,
        label: EXCHANGES[exchange].label,
        color: chartColors[exchange],
        last: typeof last === "number" ? last : undefined,
        bestBid: typeof bestBid === "number" && Number.isFinite(bestBid) ? bestBid : undefined,
        bestAsk: typeof bestAsk === "number" && Number.isFinite(bestAsk) ? bestAsk : undefined,
        updatedAt: isFresh && typeof price?.ts === "number" && Number.isFinite(price.ts) ? price.ts : undefined,
      };
    });
  }, [orderBooks, prices, stale]);

  const scrollToSection = useCallback((sectionId: SectionId) => {
    if (typeof window === "undefined") {
      return;
    }
    const nav = document.querySelector("nav");
    const navHeight = nav instanceof HTMLElement ? nav.offsetHeight : 0;
    const offset = navHeight + 16;
    const target = document.getElementById(sectionId) ?? document.getElementById("main-content");
    if (target) {
      const rect = target.getBoundingClientRect();
      const nextTop = Math.max(0, window.scrollY + rect.top - offset);
      window.scrollTo({ top: nextTop, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const handleNavClick = useCallback(
    (sectionId: SectionId, closeMenu = false) => () => {
      if (forceActiveSection && sectionId !== forceActiveSection) {
        return;
      }

      setActiveSection(sectionId);
      if (sectionId === "lottery-bitcoin") {
        setLotteryPageVariant("panel");
      }
      if (closeMenu) {
        setMenuOpen(false);
      }
      if (typeof window === "undefined") {
        return;
      }
      const { pathname, search } = window.location;
      window.history.replaceState(null, "", `${pathname}${search}#${sectionId}`);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          scrollToSection(sectionId);
        });
      });
    },
    [forceActiveSection, scrollToSection, setMenuOpen],
  );

  const footerIconButtonClass =
    "inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-700/70 bg-slate-900/60 text-slate-100 transition hover:border-slate-500 hover:text-slate-50";

  const createReferralAwareHref = useCallback(
    (baseUrl: string): string => {
      if (!referralCode) {
        return baseUrl;
      }
      try {
        const fallbackOrigin =
          typeof window !== "undefined" && window.location?.origin
            ? window.location.origin
            : "https://cryptochange.ir";
        const url = new URL(baseUrl, baseUrl.startsWith("http") ? undefined : fallbackOrigin);
        const hostname = url.hostname.toLowerCase();
        if (hostname === "t.me" || hostname.endsWith(".t.me")) {
          const existingStart = url.searchParams.get("start");
          const startParam = buildMiniAppStartParamWithReferral(existingStart, referralCode);
          if (startParam) {
            url.searchParams.set("start", startParam);
          }
          return url.toString();
        }
        url.searchParams.set("ref", referralCode);
        return url.toString();
      } catch {
        const hashIndex = baseUrl.indexOf("#");
        const hasHash = hashIndex >= 0;
        const beforeHash = hasHash ? baseUrl.slice(0, hashIndex) : baseUrl;
        const hashPart = hasHash ? baseUrl.slice(hashIndex) : "";
        const separator = beforeHash.includes("?") ? "&" : "?";
        return `${beforeHash}${separator}ref=${encodeURIComponent(referralCode)}${hashPart}`;
      }
    },
    [referralCode],
  );

  const referralAwareLinks = useMemo(() => {
    return {
      bot: createReferralAwareHref(TELEGRAM_BOT_URL),
      channel: createReferralAwareHref(TELEGRAM_CHANNEL_URL),
      contact: createReferralAwareHref(TELEGRAM_CONTACT_URL),
      site: createReferralAwareHref(CRYPTOCHANGE_WEB_URL),
    };
  }, [createReferralAwareHref]);

  const setLotteryVariantForSection = useCallback(
    (section: SectionId | null, isLeaderboard: boolean) => {
      if (section === "account") {
        setLotteryPageVariant("account");
      } else if (section === "lottery-bitcoin") {
        setLotteryPageVariant(isLeaderboard ? "leaderboard" : "panel");
      }
    },
    [],
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const hasReferralParam = params.has("ref");
    const rawHash = window.location.hash.replace(/^#/, "");
    const hashIsLeaderboard = rawHash === "lottery-leaderboard";
    const normalizedHash = hashIsLeaderboard ? "lottery-bitcoin" : rawHash;
    const desiredSection: SectionId | null = hasReferralParam && BLOCKS_LOTTERY_ENABLED
      ? "lottery-bitcoin"
      : isNavigableSection(normalizedHash)
        ? normalizedHash
        : legacyLotteryLeaderboardPath
          ? "lottery-bitcoin"
          : null;

    const shouldShowLeaderboard = hashIsLeaderboard || legacyLotteryLeaderboardPath;
    setLotteryVariantForSection(desiredSection, shouldShowLeaderboard);

    if (shouldShowLeaderboard && !hashIsLeaderboard) {
      const { search, pathname } = window.location;
      const basePath = pathname === "/lottery-leaderboard" ? "/" : pathname;
      window.history.replaceState(null, "", `${basePath}${search}#lottery-leaderboard`);
    }

    if (desiredSection) {
      setActiveSection(desiredSection);
      const { pathname, search } = window.location;
      const targetHash = desiredSection === "account"
        ? "account"
        : shouldShowLeaderboard
          ? "lottery-leaderboard"
          : desiredSection;
      window.history.replaceState(null, "", `${pathname}${search}#${targetHash}`);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          scrollToSection(desiredSection);
        });
      });
    }

    const handleHashChange = () => {
      const nextHash = window.location.hash.replace(/^#/, "");
      const nextIsLeaderboard = nextHash === "lottery-leaderboard";
      const normalizedHashValue = nextIsLeaderboard ? "lottery-bitcoin" : nextHash;
      if (isNavigableSection(normalizedHashValue)) {
        setLotteryVariantForSection(normalizedHashValue, nextIsLeaderboard);
      }
      if (isNavigableSection(normalizedHashValue)) {
        const nextSection = normalizedHashValue;
        setActiveSection(nextSection);
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            scrollToSection(nextSection);
          });
        });
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [
    forceActiveSection,
    legacyLotteryLeaderboardPath,
    scrollToSection,
    setActiveSection,
    setLotteryVariantForSection,
  ]);

  const connectorsFailed = connectorsError !== null;
  const showLoadingIndicator = false; // اگر نیاز به loading indicator داشتید می‌تونید true کنید
  const isLotterySection = BLOCKS_LOTTERY_ENABLED
    && (currentSection === "lottery-bitcoin" || currentSection === "account");
  const activeLotteryTab: LotteryNavActiveTab = lotteryPageVariant === "leaderboard"
    ? "leaderboard"
    : lotteryPageVariant === "account"
      ? "account"
      : "lottery";
  const lotterySectionId = currentSection === "account" ? "account" : "lottery-bitcoin";
  const lotteryFallbackText = activeLotteryTab === "leaderboard"
    ? "در حال آماده‌سازی جدول برترین‌ها..."
    : activeLotteryTab === "account"
      ? "در حال بارگذاری حساب کاربری..."
      : "در حال آماده‌سازی پنل قرعه‌کشی بیت‌کوین...";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 pb-28 text-slate-50 scroll-smooth">
      {connectorsFailed ? (
        <div className="mx-auto mt-4 flex max-w-xl flex-col gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200">
          <div className="font-semibold">اتصال زنده برقرار نشد</div>
          <p className="leading-relaxed text-amber-100/90">
            دریافت‌کننده‌های قیمت با مشکل مواجه شدند. لطفاً اتصال اینترنت خود را بررسی کنید و دوباره تلاش نمایید. داده‌های کش‌شده ممکن است قدیمی باشند.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleRetryConnectors}
              className="inline-flex items-center gap-2 rounded-md border border-amber-400/60 bg-transparent px-3 py-1.5 text-sm font-medium text-amber-100 transition hover:bg-amber-500/20"
            >
              تلاش دوباره
            </button>
          </div>
        </div>
      ) : null}
      {showLoadingIndicator ? (
        <div className="mx-auto mt-4 flex max-w-xl items-center justify-center gap-3 rounded-lg border border-slate-800/60 bg-slate-900/60 p-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-600 border-t-sky-500"></div>
          <p className="text-sm text-slate-300">در حال اتصال به صرافی‌ها...</p>
        </div>
      ) : null}


      <nav className="app-nav z-50 border-b border-slate-800/70 bg-slate-950/80 backdrop-blur-md" role="navigation" aria-label="Primary">
        <div
          className="mx-auto flex max-w-6xl flex-row-reverse items-center justify-between gap-3 px-4 py-2 md:py-2.5"
        >
          <a
            href="/"
            className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-slate-800/70 bg-slate-900/40 px-2.5 py-1.5 text-[0.7rem] font-semibold uppercase tracking-[0.32em] text-slate-200 transition hover:border-slate-700 hover:bg-slate-900/60 focus-visible:border-sky-500/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/60"
            aria-label="cryptochange.ir – کریپتوچنج"
            onClick={(event) => {
              event.preventDefault();
              handleNavClick(DEFAULT_SECTION)();
            }}
          >
            <img
              src="/icons/icon-192.png"
              alt="لوگو کریپتوچنج"
              width={40}
              height={40}
              loading="lazy"
              className="h-8 w-8 rounded-xl transition-all duration-200"
            />
            <span className="whitespace-nowrap text-sky-200 leading-tight">cryptochange.ir</span>
          </a>
          <div className="flex flex-none items-center justify-end gap-3 md:flex-1 md:justify-start">
            <div className="flex items-center justify-end gap-3 md:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md border border-slate-800/60 bg-slate-900/40 p-2 text-slate-200 transition hover:border-slate-700 hover:text-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/60"
                aria-label="Toggle menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {menuOpen ? (<path d="M18 6L6 18M6 6l12 12" />) : (<><path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" /></>)}
                </svg>
              </button>
            </div>

            <div className="hidden items-center gap-1 sm:gap-2 md:flex">
              <button
                type="button"
                onClick={handleNavClick('live')}
                className={`inline-flex items-center gap-2 rounded-md px-2 py-1 text-xs sm:px-3 ${currentSection === 'live' ? 'bg-slate-800/60 text-slate-100' : 'text-slate-200 hover:bg-slate-800/60 hover:text-slate-50'}`}
                aria-pressed={currentSection === 'live'}
              >
                <ChartLineIcon className="h-4 w-4" aria-hidden />
                <span>قیمت لحظه‌ای</span>
              </button>
              <button
                type="button"
                onClick={handleNavClick('comparison')}
                className={`inline-flex items-center gap-2 rounded-md px-2 py-1 text-xs sm:px-3 ${currentSection === 'comparison' ? 'bg-slate-800/60 text-slate-100' : 'text-slate-200 hover:bg-slate-800/60 hover:text-slate-50'}`}
                aria-pressed={currentSection === 'comparison'}
              >
                <CompareIcon className="h-4 w-4" aria-hidden />
                <span>مقایسه صرافی‌ها</span>
              </button>
              <button
                type="button"
                onClick={handleNavClick('calculator')}
                className={`inline-flex items-center gap-2 rounded-md px-2 py-1 text-xs sm:px-3 ${currentSection === 'calculator' ? 'bg-slate-800/60 text-slate-100' : 'text-slate-200 hover:bg-slate-800/60 hover:text-slate-50'}`}
                aria-pressed={currentSection === 'calculator'}
              >
                <CalculatorIcon className="h-4 w-4" aria-hidden />
                <span>ماشین‌حساب</span>
              </button>
              <button
                type="button"
                onClick={BLOCKS_LOTTERY_ENABLED ? handleNavClick('lottery-bitcoin') : undefined}
                disabled={!BLOCKS_LOTTERY_ENABLED}
                className={`inline-flex items-center gap-2 rounded-md px-2 py-1 text-xs sm:px-3 ${
                  BLOCKS_LOTTERY_ENABLED
                    ? (currentSection === 'lottery-bitcoin' || currentSection === 'account' ? 'bg-slate-800/60 text-slate-100' : 'text-slate-200 hover:bg-slate-800/60 hover:text-slate-50')
                    : 'cursor-not-allowed text-slate-500/70'
                }`}
                aria-pressed={BLOCKS_LOTTERY_ENABLED ? currentSection === 'lottery-bitcoin' || currentSection === 'account' : undefined}
                aria-disabled={!BLOCKS_LOTTERY_ENABLED}
                title={BLOCKS_LOTTERY_ENABLED ? undefined : LOTTERY_DISABLED_HINT}
              >
                <PurchaseBagIcon className="h-4 w-4" aria-hidden />
                <span>قرعه‌کشی بیت‌کوین</span>
              </button>
            </div>

          </div>
        </div>
        <div className={`mx-auto w-full max-w-6xl px-4 pb-2 md:hidden ${menuOpen ? 'block' : 'hidden'}`}>
          <div className="mt-2 flex flex-col gap-2 rounded-lg border border-slate-800/70 bg-slate-900/70 p-2 text-slate-200">
            <button
              type="button"
              onClick={handleNavClick('live', true)}
              className={`inline-flex items-center justify-between rounded-md px-3 py-2 text-sm ${currentSection === 'live' ? 'bg-slate-800/60 text-slate-100' : 'text-slate-200 hover:bg-slate-800/60 hover:text-slate-50'}`}
              aria-pressed={currentSection === 'live'}
            >
              <span className="inline-flex items-center gap-2"><ChartLineIcon className="h-4 w-4" />قیمت لحظه‌ای</span>
            </button>
            <button
              type="button"
              onClick={handleNavClick('comparison', true)}
              className={`inline-flex items-center justify-between rounded-md px-3 py-2 text-sm ${currentSection === 'comparison' ? 'bg-slate-800/60 text-slate-100' : 'text-slate-200 hover:bg-slate-800/60 hover:text-slate-50'}`}
              aria-pressed={currentSection === 'comparison'}
            >
              <span className="inline-flex items-center gap-2"><CompareIcon className="h-4 w-4" />مقایسه صرافی‌ها</span>
            </button>
            <button
              type="button"
              onClick={handleNavClick('calculator', true)}
              className={`inline-flex items-center justify-between rounded-md px-3 py-2 text-sm ${currentSection === 'calculator' ? 'bg-slate-800/60 text-slate-100' : 'text-slate-200 hover:bg-slate-800/60 hover:text-slate-50'}`}
              aria-pressed={currentSection === 'calculator'}
            >
              <span className="inline-flex items-center gap-2"><CalculatorIcon className="h-4 w-4" />ماشین‌حساب</span>
            </button>
            <button
              type="button"
              onClick={BLOCKS_LOTTERY_ENABLED ? handleNavClick('lottery-bitcoin', true) : undefined}
              disabled={!BLOCKS_LOTTERY_ENABLED}
              className={`inline-flex items-center justify-between rounded-md px-3 py-2 text-sm ${
                BLOCKS_LOTTERY_ENABLED
                  ? (currentSection === 'lottery-bitcoin' || currentSection === 'account' ? 'bg-slate-800/60 text-slate-100' : 'text-slate-200 hover:bg-slate-800/60 hover:text-slate-50')
                  : 'cursor-not-allowed text-slate-500/70'
              }`}
              aria-pressed={BLOCKS_LOTTERY_ENABLED ? currentSection === 'lottery-bitcoin' || currentSection === 'account' : undefined}
              aria-disabled={!BLOCKS_LOTTERY_ENABLED}
              title={BLOCKS_LOTTERY_ENABLED ? undefined : LOTTERY_DISABLED_HINT}
            >
              <span className="inline-flex items-center gap-2"><PurchaseBagIcon className="h-4 w-4" />قرعه‌کشی بیت‌کوین</span>
              {BLOCKS_LOTTERY_ENABLED ? null : (
                <span className="rounded-full border border-slate-700/70 px-2 py-0.5 text-[10px] text-slate-400">
                  {LOTTERY_DISABLED_BADGE}
                </span>
              )}
            </button>
          </div>
        </div>
      </nav>
      <main id="main-content" className="mx-auto mt-10 flex max-w-6xl flex-col gap-12 px-6">
        {currentSection === "live" ? (
          <section id="live" aria-label="قیمت لحظه‌ای" aria-live="polite" className="scroll-mt-28 space-y-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-100">
              <ChartLineIcon className="h-5 w-5 text-sky-300" aria-hidden />
              <span>قیمت لحظه‌ای بیت‌کوین به تومان (BTC/IRT)</span>
            </h2>
            <div className="grid gap-6 md:grid-cols-3">
              {(Object.keys(EXCHANGES) as ExchangeId[]).map((exchange) => {
                const price = prices[exchange];
                const config = EXCHANGES[exchange];
                const midValue = midPrices[exchange];
                const deviationPct = typeof averageMid === "number" && typeof midValue === "number" && averageMid > 0
                  ? ((midValue - averageMid) / averageMid) * 100
                  : undefined;
                return (
                  <PriceCard
                    key={exchange}
                    title={config.label}
                    logo={config.logo}
                    logoWebp={config.logoWebp}
                    link={config.referralUrl}
                    testId={`price-${exchange}`}
                    price={price?.last}
                    health={health[exchange]}
                    change={change[exchange]}
                    updatedAt={price?.ts}
                    averageDiffPct={deviationPct}
                    isStale={stale[exchange]}
                    showStaleBadge={staleBadges[exchange]}
                  />
                );
              })}
            </div>
            <Suspense
              fallback={(
                <SectionSkeleton>در حال بارگذاری نمودار مقایسه لحظه‌ای صرافی‌ها...</SectionSkeleton>
              )}
            >
              <LivePriceComparison rows={livePriceRows} />
            </Suspense>
          </section>
        ) : null}

        {currentSection === "comparison" ? (
          <section id="comparison" className="scroll-mt-28 space-y-8">
            <ExchangeComparisonTable
              rows={comparisonRows}
              mode={tradeMode}
              btcPriceToman={averageMid}
            />
          </section>
        ) : null}

        {currentSection === "calculator" ? (
          <section id="calculator" className="scroll-mt-28 grid gap-8 lg:grid-cols-2">
            <div className="order-1 lg:order-1">
              <ConversionMatrix btcPriceToman={averageMid} usdtToman={averageUsdt} />
            </div>
            <div className="order-2 lg:order-2">
              <Suspense
                fallback={(
                  <SectionSkeleton>در حال بارگذاری محتوای سرگرمی روزانه...</SectionSkeleton>
                )}
              >
              <DailyMeme />
            </Suspense>
          </div>
        </section>
      ) : null}

        {isLotterySection ? (
          <section id={lotterySectionId} className="scroll-mt-28 space-y-6">
            <Suspense fallback={<SectionSkeleton>{lotteryFallbackText}</SectionSkeleton>}>
              {activeLotteryTab === "leaderboard" ? (
                <LotteryLeaderboardPage />
              ) : activeLotteryTab === "account" ? (
                <WalletPanel />
              ) : (
                <LotteryPanel satToTomanOverride={averageSatToToman} />
              )}
            </Suspense>
            <LotteryBottomNav activeTab={activeLotteryTab} />
          </section>
        ) : null}
      </main>

      <footer className="mx-auto mt-16 max-w-6xl px-6 text-sm text-slate-300">
        <div className="flex flex-col items-center gap-4 text-center md:flex-row md:justify-between md:text-right">
          <p className="text-slate-200 text-xs">چیزایی که میبینید میتونن درست نباشن و هرچیزی ممکنه هر لحظه تغییر کنه و از بین بره. پس ما مسئولیتی روی هیچی نداریم و شمام خیلی روش حساب نکنید!</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleHardReset}
              className={footerIconButtonClass}
              aria-label="بازنشانی کامل برنامه"
              title="بازنشانی کامل برنامه"
            >
              <RotateCcwIcon className="h-4 w-4" aria-hidden />
            </button>
            <a
              href={referralAwareLinks.site}
              target="_blank"
              rel="noopener noreferrer"
              className={footerIconButtonClass}
              aria-label="وب‌سایت کریپتوچنج"
              title="وب‌سایت کریپتوچنج"
            >
              <GlobeIcon className="h-4 w-4" aria-hidden />
            </a>
            {BLOCKS_TELEGRAM_BOT_ENABLED ? (
              <a
                href={referralAwareLinks.bot}
                target="_blank"
                rel="noopener noreferrer"
                className={footerIconButtonClass}
                aria-label="ربات تلگرام کریپتوچنج"
                title="ربات تلگرام کریپتوچنج"
              >
                <BotIcon className="h-4 w-4" aria-hidden />
              </a>
            ) : null}
            <a
              href={referralAwareLinks.channel}
              target="_blank"
              rel="noopener noreferrer"
              className={footerIconButtonClass}
              aria-label="کانال تلگرام ایران‌کوین"
              title="کانال تلگرام ایران‌کوین"
            >
              <SendIcon className="h-4 w-4" aria-hidden />
            </a>
            <a
              href={TELEGRAM_CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={footerIconButtonClass}
              aria-label="ارتباط با پشتیبانی در تلگرام"
              title="ارتباط با پشتیبانی در تلگرام"
            >
              <PhoneIcon className="h-4 w-4" aria-hidden />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function MainApp({
  initialLotteryPageVariant = "panel",
  forceActiveSection,
  legacyLotteryLeaderboardPath = false,
}: {
  initialLotteryPageVariant?: "panel" | "leaderboard" | "account";
  forceActiveSection?: SectionId;
  legacyLotteryLeaderboardPath?: boolean;
}) {
  return (
    <PublicBlocksExperienceProvider>
      <MainAppContent
        initialLotteryPageVariant={initialLotteryPageVariant}
        forceActiveSection={forceActiveSection}
        legacyLotteryLeaderboardPath={legacyLotteryLeaderboardPath}
      />
    </PublicBlocksExperienceProvider>
  );
}
